/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================
* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)
Coded by www.creative-tim.com
 =========================================================
* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useContext, useEffect } from 'react'

// @mui core components
import {
  Box,
  Card,
  Grid,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Fade,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import HelpIcon from '@mui/icons-material/Help'

// Settings page components
import FormField from 'components/FormField'

// Data
import { IntakeContext } from 'context/IntakeContext'
import { selectData } from 'layouts/onboarding'

// Popup
import { toast } from 'react-toastify'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'

import { useState } from 'react'

// Debounce
import { useFormDebounce } from 'hooks/useFormDebounce'
import styled, { keyframes } from 'styled-components'
import LoopIcon from '@mui/icons-material/Loop'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import OnboardingMultiSelectTile from 'components/OnboardingMultiSelectTile'

import SoftTypography from 'components/SoftTypography'
import OnboardingMultiOptionTile from 'components/OnboardingMultiOptionTile'
import { format, subYears, differenceInYears } from 'date-fns'

const rotatingIcon = keyframes`
  0% {
    transform: rotate(360deg);
  }
  100% {
    transform: rotate(0deg);
  }
`
const colorChangeIcon = keyframes`
  0% {
    color: #66d432;
  }
  50% {
    color: #66d432;
  }
  100% {
    color: #344767;
  }
`
const ChangesSavedIcon = styled(CheckCircleOutlineIcon)`
  &.saved {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 2s ${colorChangeIcon} ease-out;
  }
`
const SavingIcon = styled(LoopIcon)`
  &.loading {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 1.5s ${rotatingIcon} linear infinite;
  }
`

function Healthcare() {
  const { intakeState, updateIntakeStateAtKey, updateUser } =
    useContext(IntakeContext)

  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()

  // add an onSubmit button and call the context function `updateUser`
  // const onSubmit = () => updateUser()
  const onSubmit = async () => {
    await updateUser()
    toast.info('You have successfully updated your healthcare details', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }

  useEffect(() => {
    // console.log(intakeState)
  }, [intakeState])

  const healthInsurance = intakeState?.healthInsurance

  return (
    <Card
      sx={{
        textAlign: 'left',
        boxShadow: 0,
        border: '1px solid #ddd',
        borderRadius: 2,
      }}
    >
      <Box px={3} pt={3} display="flex" justifyContent="space-between">
        <Typography variant="h3" fontSize={{ xs: '24px', md: '30px' }}>
          Health Insurance
        </Typography>

        <Button
          variant="gradient"
          color="dark"
          sx={{ height: '100%' }}
          onClick={onSubmit}
          startIcon={
            loading ? (
              <>
                <SavingIcon fontSize="medium" className={'loading'} />
              </>
            ) : (
              <>
                <ChangesSavedIcon fontSize="medium" className={'saved'} />
              </>
            )
          }
        >
          {loading ? `updating...` : 'up to date'}
        </Button>
      </Box>
      <Box component="form" pb={3} px={3}>
        <Grid container columnSpacing={3} rowSpacing={2}>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'healthInsurance',
              label: 'Which health insurance program are you part of?',
              placeholder: '',
              inputType: 'select',
              value: intakeState.healthInsurance,
              onChange: updateIntakeStateAndSubmit((healthInsurance) =>
                updateIntakeStateAtKey({ healthInsurance })
              ),
              options: selectData.healthInsurance,
            })}
          </Grid>

          {healthInsurance === 'fehb' || healthInsurance === 'pshb' ? (
            <Grid item xs={12} md={8}>
              {renderMuiFormField({
                autoFocus: false,
                id: 'fehbCode',
                label: `Select Your ${intakeState?.healthInsurance.toUpperCase()} Code`,
                placeholder: '',
                inputType: 'select',
                value: intakeState.fehbCode,
                onChange: updateIntakeStateAndSubmit((fehbCode) =>
                  updateIntakeStateAtKey({ fehbCode })
                ),
                options:
                  healthInsurance === 'fehb'
                    ? selectData.fehbCode.slice(0, 438)
                    : selectData.fehbCode.slice(-234),
              })}
            </Grid>
          ) : (
            <></>
          )}

          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'healthInsurancePremium',
              label: 'What is your healthcare premium per pay period?',
              placeholder: '$275.00',
              inputType: 'currencyFloat',
              value: intakeState.healthInsurancePremium,
              onChange: updateIntakeStateAndSubmit((healthInsurancePremium) =>
                updateIntakeStateAtKey({ healthInsurancePremium })
              ),
            })}
          </Grid>
        </Grid>
      </Box>
    </Card>
  )
}

export default Healthcare
